import ldCloneDeep from 'lodash/cloneDeep';
import { Dispatch } from 'redux';
import { actions } from '../../modules/Actions';
import { LOADING_TYPE } from '../../reducers/LoadingStatusReducer';
import {
  GetVisitSummaryApiRequest,
  GetVisitSummaryApiResponse,
  getVisitSummaryApi,
  VisitReservationDetails,
  WorkInfoForGetSchedule,
} from '../../../external/VisitApi';
import { API_RESULT_CD } from '../../../external/Common';
import { API_CODE, API_STATE } from '../../reducers/SorryMessageReducer/functions';
import { GetHndlItmMngApiRequest, getHndlItmMngList, HndlItmMngMst } from '../../../external/HndlItmMngApi';
import {
  VisitReservationInfo,
  VisitReservationListInfo,
  VisitReservationStoreInfo,
  VisitReservationSummaryAndListInfo,
  VisitReservationSummaryInfo,
} from '../../reducers/VisitReservationInfoReducer';
import { MasterInfoState, getPcmCdNmByPcmCdDivisionFromMasterInfo } from '../../reducers/MasterInfoReducer';
import { PCM_CD_DIVISION } from '../../../redux/reducers/MasterInfoReducer/definitions/PcmCdDivision';
import {
  BEF_CALL_TYPE,
  HEARING_QA_TYPE,
  HEARING_TYPE,
  LINE_CONTACT_TYPE,
  REF_AGREE_STATUS_TYPE,
  WEB_INDENTIFICATION_TYPE,
} from '../../../Common';

const SORT_FIELDS = ['kanaNm', 'longNm', 'shortNm', 'companyCfc'] as const;

/**
 * 取扱商材管理取得APIから来店予約照会が可能な店舗リストを取得する。
 * @param request
 * @param dispatch
 * @returns
 */
export const getVisitStoreList = async (request: GetHndlItmMngApiRequest, dispatch: Dispatch): Promise<VisitReservationStoreInfo[]> => {
  dispatch(actions.updateLoadingStatus.started(LOADING_TYPE.FETCH_VISIT_RESERVATION_STORE_LIST));
  const visitReservationStoreInfo = new Array<VisitReservationStoreInfo>();
  const response = await getHndlItmMngList(request);
  if (response.result.resultCd === API_RESULT_CD.OK) {
    if (response.hndlItmMngMstList && response.hndlItmMngMstList.length > 0) {
      visitReservationStoreInfo.push(...response.hndlItmMngMstList);
      const sortedStoreList: HndlItmMngMst[] = ldCloneDeep(response.hndlItmMngMstList).sort((a, b) => {
        for (const sortField of SORT_FIELDS) {
          const compare = a[sortField].localeCompare(b[sortField], 'ja');
          if (compare !== 0) return compare;
        }
        return a.chkdegtOrdcstmCd.localeCompare(b.chkdegtOrdcstmCd, 'ja');
      });
      dispatch(actions.visitReservationInfo.updateStoreList(sortedStoreList));
    } else {
      // 店舗リストが取得できなかった場合、ストアの店舗リストを初期化する
      dispatch(actions.visitReservationInfo.updateStoreList([]));
    }
  } else {
    dispatch(
      actions.updateErrMessage.errorDetail({
        functionId: API_STATE.GET_HNDL_ITM_MNG_LIST,
        apiState: API_CODE.AP122,
        result: response.result,
      }),
    );
  }
  dispatch(actions.updateLoadingStatus.done({ params: LOADING_TYPE.FETCH_VISIT_RESERVATION_STORE_LIST }));
  return visitReservationStoreInfo;
};

/**
 * 来店予約サマリ・一覧取得APIを実行
 * @param request
 * @param
 * @param dispatch
 * @returns
 */
export const getVisitSummary = async (
  request: GetVisitSummaryApiRequest,
  masterInfo: MasterInfoState,
  dispatch: Dispatch,
): Promise<VisitReservationSummaryAndListInfo | undefined> => {
  let visitReservationSummaryAndListInfo = undefined;
  dispatch(actions.updateLoadingStatus.started(LOADING_TYPE.VISIT_RESERVATION));
  const response = await getVisitSummaryApi(request);

  if (response.result.resultCd === API_RESULT_CD.OK) {
    const summaryInfo = createSummaryViewData(request, response, masterInfo);
    const listInfo = createListViewData(response, masterInfo);
    // ストアへ情報保持
    dispatch(actions.visitReservationInfo.updateGetSummaryApiRequest(request));
    dispatch(actions.visitReservationInfo.updateSummaryInfo(summaryInfo));
    dispatch(actions.visitReservationInfo.updateListInfo(listInfo));
    // 更新ボタン挙動更新
    updateRefresh(request, masterInfo, dispatch);

    visitReservationSummaryAndListInfo = {
      summaryInfo: JSON.parse(JSON.stringify(summaryInfo)),
      listInfo: JSON.parse(JSON.stringify(listInfo)),
    };

    dispatch(
      actions.updateLoadingStatus.done({
        params: LOADING_TYPE.VISIT_RESERVATION,
        result: response.result,
      }),
    );
  } else {
    dispatch(
      actions.updateErrMessage.errorDetail({
        functionId: API_STATE.GET_VISIT_SUMMARY,
        apiState: API_CODE.AP123,
        result: response.result,
      }),
    );
    dispatch(
      actions.updateLoadingStatus.failed({
        params: LOADING_TYPE.VISIT_RESERVATION,
        error: response.result,
      }),
    );
  }
  return visitReservationSummaryAndListInfo;
};

/**
 * 来店予約サマリ・一覧取得APIの再実行
 * @param summaryReq 来店予約サマリ・一覧取得APIのリクエスト
 * @param masterInfo コードマスタ
 * @param dispatch
 */
export const updateRefresh = (summaryReq: GetVisitSummaryApiRequest, masterInfo: MasterInfoState, dispatch: Dispatch) => {
  /* istanbul ignore next */
  const refresh = async () => {
    if (summaryReq.visitSummaryInfoList.length > 0) {
      await getVisitSummary(summaryReq, masterInfo, dispatch);
    }
  };
  dispatch(actions.updateRefresh({ refresh }));
};

/**
 * 来店予約サマリに表示するデータを作成
 * @param request
 * @param response 来店予約サマリ・一覧取得APIのレスポンス
 * @param masterInfo
 */
const createSummaryViewData = (
  request: GetVisitSummaryApiRequest,
  response: GetVisitSummaryApiResponse,
  masterInfo: MasterInfoState,
): VisitReservationSummaryInfo => {
  const searchDate = request.visitSummaryInfoList[0].scheduleStartDate.split('/').join('');
  const currentDate = response.result.jobDateTime.substring(0, 8);
  // 予約数WEBの表示設定
  const onlineReservationlist = [
    {
      label: '予約数',
      count: response.visitSummaryResultDto ? response.visitSummaryResultDto.webReservationCount : 0,
    },
  ];

  // 予約数クルーの表示設定
  const crewReservationlist = [
    {
      label: '予約数',
      count: response.visitSummaryResultDto ? response.visitSummaryResultDto.crewReservationCount : 0,
    },
  ];
  // 検索日付が当日の場合のみ、「うち当日」を表示する
  if (searchDate === currentDate) {
    onlineReservationlist.push({
      label: 'うち当日',
      count: response.visitSummaryResultDto ? response.visitSummaryResultDto.webReservationDayCount : 0,
    });
    crewReservationlist.push({
      label: 'うち当日',
      count: response.visitSummaryResultDto ? response.visitSummaryResultDto.crewReservationDayCount : 0,
    });
  }

  // 事前準備架電の表示項目初期設定
  const befCalllist = [
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.NOT_IMPLEMENTED),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.RE_CALL),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.PERFORMED),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.BEF_CALL, BEF_CALL_TYPE.UN_NECESSARY),
      count: 0,
    },
  ];
  // 事前準備クルーヒアリングの表示項目初期設定
  const advancePreparationCrewHearinglist = [
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.HEARING, HEARING_TYPE.NOT_IMPLEMENTED),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.HEARING, HEARING_TYPE.PERFORMED),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.HEARING, HEARING_TYPE.NOT_APPLICABLE),
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.HEARING, HEARING_TYPE.UN_NECESSARY),
      count: 0,
    },
  ];

  // 事前準備架電の表示項目初期設定
  const advancePreparationInquirylist = [
    {
      label: '未実施',
      count: 0,
    },
    {
      label: '未同意',
      count: 0,
    },
    {
      label: '実施済',
      count: 0,
    },
    {
      label: getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, PCM_CD_DIVISION.REF_AGREE_STATUS, REF_AGREE_STATUS_TYPE.NOT_APPLICABLE),
      count: 0,
    },
  ];

  // 架電/LINE
  const lineContactList = [
    {
      label: '連絡可',
      count: 0,
    },
    {
      label: '連絡済',
      count: 0,
    },
    {
      label: '連絡中',
      count: 0,
    },
    {
      label: '要確認',
      count: 0,
    },
    {
      label: '対象外',
      count: 0,
    },
  ];

  /* istanbul ignore next */
  if (response) {
    // 事前準備架電の表示設定
    response?.visitSummaryResultDto?.befCallDto.map((item) => {
      switch (item.befCallCode) {
        case BEF_CALL_TYPE.NOT_IMPLEMENTED:
          befCalllist[0].count = item.befCallCount;
          break;
        case BEF_CALL_TYPE.PERFORMED:
          befCalllist[2].count = item.befCallCount;
          break;
        case BEF_CALL_TYPE.UN_NECESSARY:
          befCalllist[3].count = item.befCallCount;
          break;
        case BEF_CALL_TYPE.RE_CALL:
          befCalllist[1].count = item.befCallCount;
          break;
        default:
      }
    });
    // 事前準備クルーヒアリングの表示設定
    response?.visitSummaryResultDto?.hearingDto.map((item) => {
      switch (item.hearingCode) {
        case HEARING_TYPE.NOT_IMPLEMENTED:
          advancePreparationCrewHearinglist[0].count = item.hearingCount;
          break;
        case HEARING_TYPE.PERFORMED:
          advancePreparationCrewHearinglist[1].count = item.hearingCount;
          break;
        case HEARING_TYPE.UN_NECESSARY:
          advancePreparationCrewHearinglist[3].count = item.hearingCount;
          break;
        case HEARING_TYPE.NOT_APPLICABLE:
          advancePreparationCrewHearinglist[2].count = item.hearingCount;
          break;
        default:
      }
    });

    // 事前準備照会の表示設定
    response?.visitSummaryResultDto?.refAgreeStatusDto.map((item) => {
      switch (item.refAgreeStatusCode) {
        case REF_AGREE_STATUS_TYPE.CONSENT:
          advancePreparationInquirylist[2].count = item.refAgreeStatusCount;
          break;
        case REF_AGREE_STATUS_TYPE.DISAGEEMENT:
          advancePreparationInquirylist[1].count = item.refAgreeStatusCount;
          break;
        case REF_AGREE_STATUS_TYPE.UN_CONFIRMED:
          advancePreparationInquirylist[0].count = item.refAgreeStatusCount;
          break;
        case REF_AGREE_STATUS_TYPE.NOT_APPLICABLE:
          advancePreparationInquirylist[3].count = item.refAgreeStatusCount;
          break;
        default:
      }
    });

    // LINEステータス
    response?.visitSummaryResultDto?.lineContactDtos.map((item) => {
      switch (item.lineContactCode) {
        case LINE_CONTACT_TYPE.CONTACT_AVAILABLE:
          lineContactList[0].count = item.lineContactCount;
          break;
        case LINE_CONTACT_TYPE.CONTACTED:
          lineContactList[1].count = item.lineContactCount;
          break;
        case LINE_CONTACT_TYPE.CONTACTING:
          lineContactList[2].count = item.lineContactCount;
          break;
        case LINE_CONTACT_TYPE.CONFIRMATION_REQUIRED:
          lineContactList[3].count = item.lineContactCount;
          break;
        case LINE_CONTACT_TYPE.NOT_APPLICABLE:
          lineContactList[4].count = item.lineContactCount;
          break;
        default:
      }
    });
  }

  // 時間帯別レポート表示設定
  const labels = ['8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21']
    .map((h) => [`${h}:00`, `${h}:15`, `${h}:30`, `${h}:45`])
    .flat();
  const crewList: number[] = [];
  const webList: number[] = [];
  // 時間帯ごとに集計を行う(8:00,8:15,8:30,8:45,9:00...左記の順番で繰り返し処理)
  labels.forEach((h) => {
    let crewCount = 0;
    let webCount = 0;
    response?.visitReservationDetailsDto?.map((item) => {
      // クルー件数集計する
      if (item.scheduleStartDateTime) {
        const _scheduleStartDateTime = item.scheduleStartDateTime;
        const _scheduleEndDateTime = item.scheduleEndDateTime || _scheduleStartDateTime;
        if ([WEB_INDENTIFICATION_TYPE.CREW_NOT_TODAY, WEB_INDENTIFICATION_TYPE.CREW_TODAY].includes(item.webIdentificationType || '')) {
          // scheduleStartDateTime(予約開始時刻)の形式：hh:mm:ss、時間と分ごとに集計
          isTimeLabelWithinRange(h, _scheduleStartDateTime, _scheduleEndDateTime) && crewCount++;
        } else {
          isTimeLabelWithinRange(h, _scheduleStartDateTime, _scheduleEndDateTime) && webCount++;
        }
      }
    });
    crewList.push(crewCount);
    webList.push(webCount);
  });

  return {
    onlineReservationlist: onlineReservationlist,
    crewReservationlist: crewReservationlist,
    befCalllist: befCalllist,
    advancePreparationCrewHearinglist: advancePreparationCrewHearinglist,
    advancePreparationInquirylist: advancePreparationInquirylist,
    reportCrewList: crewList,
    reportWebList: webList,
    count: response.visitReservationResultDto.count,
    lineContactList,
  };
};

/**
 * タイムスタンプが指定されたタイムラベル（分）ごとの範囲か判定
 * @param timelabel
 * @param timeStart
 * @param timeEnd
 */
const isTimeLabelWithinRange = (timelabel: string, timeStart: string, timeEnd: string) => {
  const timelabel_n = Number((timelabel + ':00').split(':').join(''));
  const timeStart_n = Number(timeStart.split(':').join(''));
  const timeEnd_n = Number(timeEnd.split(':').join(''));

  return timeStart_n <= timelabel_n && timelabel_n < timeEnd_n;
};

/**
 * 来店予約サマリに表示するデータを作成
 * @param response 来店予約サマリ・一覧取得APIのレスポンス
 */
const createListViewData = (response: GetVisitSummaryApiResponse, masterInfo: MasterInfoState): VisitReservationListInfo => {
  const DEFAULT_NAME = '-';
  const convertCodeToCodeName = (pcmCdDivision: string, code: string | undefined, defaultName = DEFAULT_NAME) => {
    if (!code) {
      return defaultName;
    }
    return getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, pcmCdDivision, code) || defaultName;
  };
  const convertHearingQRCodeToName = (code: string | undefined) => {
    return HEARING_QA_TYPE.ANSWERED === code || HEARING_QA_TYPE.NOT_ANSWERED === code
      ? convertCodeToCodeName(PCM_CD_DIVISION.HEARING_QR, code)
      : '';
  };

  return {
    list: response.visitReservationDetailsDto
      ? response.visitReservationDetailsDto.map(
          (item: VisitReservationDetails): VisitReservationInfo => ({
            sendDatetime: `${item.scheduleStartDateTime?.slice(0, 5)}-${item.scheduleEndDateTime?.slice(0, 5)}` || DEFAULT_NAME,
            code: item.shopCallNo || DEFAULT_NAME,
            business:
              item.workInfoForGetScheduleListDto?.map((workInfo: WorkInfoForGetSchedule) => workInfo.workNmIn).join(', ') || DEFAULT_NAME,
            staff: item.staffName || DEFAULT_NAME,
            route: convertCodeToCodeName(PCM_CD_DIVISION.WEB_CREW_KIND_WITH_TODAY_CHG_FLG, item.webIdentificationType),
            movie: convertCodeToCodeName(PCM_CD_DIVISION.WATCH_VIDEO_FLG, item.watchVideoFlg),
            inquiry: convertCodeToCodeName(PCM_CD_DIVISION.REF_CUSTOMER_AGREE_STATUS, item.inquiryStatus),
            call: convertCodeToCodeName(PCM_CD_DIVISION.BEF_CALL, item.befCall),
            hearing: convertCodeToCodeName(PCM_CD_DIVISION.HEARING_CREW, item.hearing),
            hearingWeb: convertCodeToCodeName(PCM_CD_DIVISION.HEARING_WEB, item.hearingWeb),
            hearingId: item.hearingId,
            firstInsDate: item.firstInsDate,
            lineContactFlg: convertCodeToCodeName(PCM_CD_DIVISION.LINE_CONTACT_FLG, item.lineContactFlg),
            hearingQr: convertHearingQRCodeToName(item.hearingQr),
          }),
        )
      : [],
    count: response.visitReservationResultDto.count,
  };
};
